/**
 * /car-paint-code — the logic, without a page.
 *
 * Everything the tool decides lives here so it can be tested in node on real
 * inputs: what a person typed (VIN, paint code, make, make + model), which
 * factory colours a code can be, and which characters on a photographed label
 * are the paint code. The page (index.js) only draws.
 *
 * The catalogue (catalog.js, generated by scripts/car-paint/build-catalog.cjs)
 * is passed in, never imported: the engine runs in node tests against a
 * fixture and in the browser against the real file.
 *
 *   brands[i] = { id, name, nameRu, aliases, label:{locations, field, codeRegex,
 *                 examples}, vin:{route, url, phone}, colors:[ROW…] }
 *   ROW       = [code, name, nameEn, nameRu, finish, family, rgb, models, years,
 *                market, srcIndex, codeAlt, ptsName, markets, altNames]
 *   — one row per (make, code): the build merges a code's names across price
 *   lists (primary + nameEn/nameRu + altNames) and its markets (primary first).
 */

export const F = { code: 0, name: 1, nameEn: 2, nameRu: 3, finish: 4, family: 5, rgb: 6, models: 7, years: 8, market: 9, src: 10, codeAlt: 11, ptsName: 12, markets: 13, altNames: 14 };

/* ── normalisation ───────────────────────────────────────────────────────── */

/* A code is typed on a Russian keyboard as often as on a Latin one, and the
   two look identical: «С»/«C», «Н»/«H», «А»/«A». Both sides of every
   comparison go through the same fold, so a UAZ code written in Cyrillic
   («ЗНА») still matches itself, and «с/tr» typed in Russian reads as C/TR. */
const CYR2LAT = {
    'А': 'A', 'В': 'B', 'С': 'C', 'Е': 'E', 'Н': 'H', 'К': 'K', 'М': 'M', 'О': 'O',
    'Р': 'P', 'Т': 'T', 'Х': 'X', 'У': 'Y',
};

export function fold(s) {
    return String(s == null ? '' : s).toUpperCase().replace(/Ё/g, 'Е')
        .replace(/[АВСЕНКМОРТХУ]/g, (c) => CYR2LAT[c] || c);
}

/** Code key: folded, and without the separators makers print inconsistently
    («NH-731P» on the sticker, «NH731P» in a price list; «F9E/9P9P»). */
export function codeKey(s) {
    return fold(s).replace(/[\s\-.·_]/g, '');
}

/* Characters OCR and people confuse on labels. Only used to SUGGEST a near
   code, never to silently accept one: an O read as 0 is a different colour. */
const LOOKALIKE = { '0': 'O', 'O': '0', '1': 'I', 'I': '1', '8': 'B', 'B': '8', '5': 'S', 'S': '5', '2': 'Z', 'Z': '2', '6': 'G', 'G': '6' };

function lookalikeVariants(key) {
    const out = new Set();
    for (let i = 0; i < key.length; i++) {
        const alt = LOOKALIKE[key[i]];
        if (alt) out.add(key.slice(0, i) + alt + key.slice(i + 1));
    }
    return out;
}

export function textKey(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/ё/g, 'е')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[«»"'`’.,:;!?()\[\]/\\]/g, ' ').replace(/\s+/g, ' ').trim();
}

/* ── the catalogue index ─────────────────────────────────────────────────── */

/** Build the lookup structures once per catalogue (and again when owners'
    submissions are merged in). */
export function indexCatalog(cat, community, labels) {
    const brands = cat.brands.slice();
    const byId = new Map();
    const aliasList = [];
    const codeIdx = new Map();          // codeKey → [{brand, row}]
    for (const b of brands) {
        byId.set(b.id, b);
        const names = new Set([b.name, b.nameRu, b.id, ...(b.aliases || [])].filter(Boolean).map(textKey));
        for (const a of names) if (a.length >= 2) aliasList.push([a, b.id]);
    }
    const addRow = (b, row) => {
        for (const c of [row[F.code], row[F.codeAlt]]) {
            if (!c) continue;
            const k = codeKey(c);
            if (!codeIdx.has(k)) codeIdx.set(k, []);
            codeIdx.get(k).push({ brand: b.id, row });
        }
    };
    for (const b of brands) for (const row of b.colors) addRow(b, row);

    /* Owners' submissions (approved in moderation) join as their own rows,
       marked by market 'owners' so the page can say where they came from.
       A submission whose code the catalogue already has for that make adds
       nothing but a model: the maker's row stays the answer. An owner types
       the model their own way («гла 200», «Granta») — it is filed under the
       catalogue's name when one fits, so the model picker finds it. */
    if (community && community.length) {
        for (const s of community) {
            const b = byId.get(s.brand);
            if (!b || !s.code) continue;
            const model = s.model ? canonModel(b, s.model) : '';
            const k = codeKey(s.code);
            const known = (codeIdx.get(k) || []).find((h) => h.brand === b.id);
            if (known) {
                if (model && known.row[F.models].indexOf(model) < 0) known.row[F.models].push(model);
                continue;
            }
            const row = [s.code, s.name || '', null, null, s.finish || 'unknown', s.family || 'other', null,
                model ? [model] : [], s.year ? [s.year, s.year] : null, 'owners', -1, null, null, null, []];
            row.owners = s.n || 1;
            b.colors.push(row);
            addRow(b, row);
        }
    }
    /* Where owners found the label (approved in moderation). Kept apart from
       the maker's own locations: the page must say whose word each one is. */
    for (const b of brands) b.ownerLabels = [];
    for (const s of labels || []) {
        const b = byId.get(s.brand);
        if (b && s.where) b.ownerLabels.push({ where: s.where, n: s.n || 1 });
    }
    for (const b of brands) b.ownerLabels.sort((x, y) => y.n - x.n);
    // longest alias first: «land rover» before «rover», «lada niva» before «lada»
    aliasList.sort((a, b) => b[0].length - a[0].length);
    return { cat, brands, byId, aliasList, codeIdx };
}

/* ── makes and models in free text ───────────────────────────────────────── */

/* Russian spellings of the models people type most. The catalogue carries the
   maker's Latin names; these map a word back to (make, model). Only models
   whose Russian name is unambiguous — «нива» belongs to LADA, not Chevrolet,
   in how people search (Chevrolet Niva is typed with the make). */
export const MODEL_ALIASES = {
    'гранта': ['lada', 'Granta'], 'веста': ['lada', 'Vesta'], 'приора': ['lada', 'Priora'],
    'калина': ['lada', 'Kalina'], 'нива': ['lada', 'Niva'], 'ларгус': ['lada', 'Largus'],
    'иксрей': ['lada', 'XRAY'], 'xray': ['lada', 'XRAY'], 'искра': ['lada', 'Iskra'],
    'ваз': ['lada', null], 'жигули': ['lada', null], 'десятка': ['lada', '2110'],
    'солярис': ['hyundai', 'Solaris'], 'крета': ['hyundai', 'Creta'], 'туссан': ['hyundai', 'Tucson'],
    'рио': ['kia', 'Rio'], 'сид': ['kia', 'Ceed'], 'спортейдж': ['kia', 'Sportage'], 'соренто': ['kia', 'Sorento'],
    'поло': ['volkswagen', 'Polo'], 'гольф': ['volkswagen', 'Golf'], 'тигуан': ['volkswagen', 'Tiguan'],
    'октавия': ['skoda', 'Octavia'], 'рапид': ['skoda', 'Rapid'], 'кодиак': ['skoda', 'Kodiaq'],
    'логан': ['renault', 'Logan'], 'сандеро': ['renault', 'Sandero'], 'дастер': ['renault', 'Duster'],
    'аркана': ['renault', 'Arkana'], 'каптюр': ['renault', 'Kaptur'],
    'фокус': ['ford', 'Focus'], 'мондео': ['ford', 'Mondeo'], 'фиеста': ['ford', 'Fiesta'],
    'камри': ['toyota', 'Camry'], 'королла': ['toyota', 'Corolla'], 'рав4': ['toyota', 'RAV4'],
    'прадо': ['toyota', 'Land Cruiser Prado'], 'крузак': ['toyota', 'Land Cruiser'],
    'лачетти': ['chevrolet', 'Lacetti'], 'круз': ['chevrolet', 'Cruze'], 'авео': ['chevrolet', 'Aveo'],
    'нива шевроле': ['chevrolet', 'Niva'], 'шнива': ['chevrolet', 'Niva'],
    'патриот': ['uaz', 'Патриот'], 'буханка': ['uaz', 'Буханка'], 'хантер': ['uaz', 'Хантер'], 'профи': ['uaz', 'Профи'],
    'patriot': ['uaz', 'Патриот'], 'hunter': ['uaz', 'Хантер'], 'profi': ['uaz', 'Профи'], 'bukhanka': ['uaz', 'Буханка'],
    'газель': ['gaz', 'Gazelle'], 'соболь': ['gaz', 'Sobol'],
    'джолион': ['haval', 'Jolion'], 'тигго': ['chery', 'Tiggo'], 'кулрей': ['geely', 'Coolray'],
    'монжаро': ['geely', 'Monjaro'], 'атлас': ['geely', 'Atlas'],
    'аутлендер': ['mitsubishi', 'Outlander'], 'лансер': ['mitsubishi', 'Lancer'], 'паджеро': ['mitsubishi', 'Pajero'],
    'икстрейл': ['nissan', 'X-Trail'], 'кашкай': ['nissan', 'Qashqai'], 'альмера': ['nissan', 'Almera'],
    'мазда 3': ['mazda', 'Mazda3'], 'мазда 6': ['mazda', 'Mazda6'], 'сх-5': ['mazda', 'CX-5'],
};

/** Find the make (and model, if named) in free text. */
export function findBrand(q, idx) {
    const t = ' ' + textKey(q) + ' ';
    for (const [alias, id] of idx.aliasList) {
        if (t.indexOf(' ' + alias + ' ') >= 0) {
            const rest = t.replace(' ' + alias + ' ', ' ').trim();
            const m = findModel(rest, id);
            return { brand: id, alias, rest: m.model ? m.rest : rest, model: m.model };
        }
    }
    const m = findModel(t.trim(), null);
    if (m.brand) return { brand: m.brand, alias: null, rest: m.rest, model: m.model };
    return null;
}

/* ── models ──────────────────────────────────────────────────────────────── */

/* Version words people type in Russian where the maker writes Latin
   («Гранта Спорт» = «Granta Sport», «Веста Кросс» = «Vesta Cross»). */
const VERSION_WORDS = {
    'спорт': 'sport', 'кросс': 'cross', 'св': 'sw', 'драйв': 'drive', 'актив': 'active', 'люкс': 'luxe',
    'классик': 'classic', 'легенд': 'legend', 'тревел': 'travel', 'бронто': 'bronto', 'инстинкт': 'instinct',
    'гибрид': 'hybrid', 'купе': 'coupe', 'кабриолет': 'cabriolet',
};

/** Model key: case, spaces, hyphens and Cyrillic look-alikes do not matter
    («CR-V» = «crv», «Mazda 3» = «MAZDA3», «4х4» typed in Russian = «4x4»). */
export function modelKey(s) {
    const words = textKey(s).split(' ').map((w) => VERSION_WORDS[w] || w);
    return fold(words.join('')).replace(/[\s\-]/g, '');
}

/* The make's own name in front of a model («LADA Granta седан») is noise in a
   list of that make's models — except where the rest would be a bare number
   (Renault «4», «5» are called «Renault 4», «Renault 5»). */
function stripMake(b, name) {
    const s = String(name || '').trim();
    for (const pre of [b.name, b.nameRu]) {
        if (!pre || s.length <= pre.length + 1) continue;
        if (s.slice(0, pre.length + 1).toLowerCase() !== pre.toLowerCase() + ' ') continue;
        const rest = s.slice(pre.length + 1);
        if (rest.length >= 3 || /[^\d\s]/.test(rest)) return rest;
    }
    return s;
}

const natural = (a, c) => a.label.localeCompare(c.label, undefined, { numeric: true, sensitivity: 'base' });

/* A first word that names no family: «Grand Santa Fe» and «Grand Starex» are
   two cars, not two versions of a «Grand». */
const NO_FAMILY_WORDS = new Set(['grand', 'new', 'super', 'the', 'all', 'nuevo', 'neue', 'nouveau']);

function commonWords(labels) {
    const split = labels.map((l) => l.split(' '));
    const out = [];
    for (let i = 0; ; i++) {
        const w = split[0][i];
        if (w == null || split.some((s) => s[i] == null || s[i].toLowerCase() !== w.toLowerCase())) break;
        out.push(w);
    }
    return out.join(' ');
}

/**
 * A make's models for the picker.
 * → { models: [{name, label, n}], families: [{label, members, n}],
 *     items: [family|model…] sorted for display, famOf: Map(name → family) }
 * A family groups the versions of one car («Granta седан», «Granta Sport»,
 * «Granta» → Granta; «C-Class», «C-Class Estate» → C-Class) — only where the
 * grouping is safe: the family name is a model itself, or a real first word
 * (four letters or more, not «Grand»). `n` counts colours, not listings.
 */
export function modelIndex(b) {
    if (b.__mi && b.__mi.rows === b.colors.length) return b.__mi;
    const count = new Map();
    for (const r of b.colors) for (const m of r[F.models] || []) count.set(m, (count.get(m) || 0) + 1);
    const models = Array.from(count.keys()).map((name) => {
        const label = stripMake(b, name);
        return { name, label, n: count.get(name), key: modelKey(label), nkey: modelKey(name) };
    });
    const labels = new Set(models.map((m) => m.label.toLowerCase()));
    const byFirst = new Map();
    for (const m of models) {
        const f = m.label.split(' ')[0].toLowerCase();
        if (!byFirst.has(f)) byFirst.set(f, []);
        byFirst.get(f).push(m);
    }
    const families = [];
    const famOf = new Map();
    for (const [first, ms] of byFirst) {
        if (ms.length < 2) continue;
        const label = commonWords(ms.map((m) => m.label));
        if (!label) continue;
        const isMake = first === String(b.name || '').toLowerCase();
        if (!labels.has(label.toLowerCase()) && (first.length < 4 || NO_FAMILY_WORDS.has(first) || isMake)) continue;
        const names = new Set(ms.map((m) => m.name));
        const n = b.colors.filter((r) => (r[F.models] || []).some((x) => names.has(x))).length;
        families.push({ label, key: modelKey(label), members: ms.slice().sort(natural), n });
        for (const m of ms) famOf.set(m.name, label);
    }
    const items = [
        ...families.map((f) => ({ type: 'f', label: f.label, n: f.n, members: f.members })),
        ...models.filter((m) => !famOf.has(m.name)).map((m) => ({ type: 'm', name: m.name, label: m.label, n: m.n })),
    ].sort(natural);
    const unmodelled = b.colors.filter((r) => !(r[F.models] || []).length).length;
    b.__mi = { rows: b.colors.length, models, families, items, famOf, unmodelled };
    return b.__mi;
}

/**
 * What a person or a link calls a model → the picker's value:
 * 'f:<family>' (all versions) or 'm:<model>' (exactly that one), or null
 * when the make's catalogue does not know the model. A family wins over the
 * model of the same name: «Granta» means every Granta, not the listing
 * that happens to be called just «LADA Granta».
 */
export function resolveModel(b, text) {
    const s = String(text == null ? '' : text).trim();
    if (!s || !b) return null;
    const mi = modelIndex(b);
    if (/^[fm]:/.test(s)) {
        const v = s.slice(2);
        const ok = s[0] === 'f' ? mi.families.some((f) => f.label === v) : count0(mi, v);
        return ok ? s : null;
    }
    const k = modelKey(s);
    if (!k) return null;
    const fam = mi.families.find((f) => f.key === k);
    if (fam) return 'f:' + fam.label;
    const m = mi.models.find((x) => x.key === k || x.nkey === k);
    if (m) return 'm:' + m.name;
    /* «Мазда 3» is typed with the make in the middle of the model's name
       («MAZDA3»), «Renault 4» likewise: try the make glued in front. */
    const km = modelKey(b.name) + k;
    const mm = mi.models.find((x) => x.key === km || x.nkey === km);
    return mm ? 'm:' + mm.name : null;
}

function count0(mi, name) { return mi.models.some((m) => m.name === name); }

/** The longest run of words in `text` that names one of the make's models.
    → { sel, label, rest } or null. «gla 200» → GLA (200 is the engine). */
export function findModelIn(b, text) {
    const toks = String(text || '').split(/\s+/).filter(Boolean);
    for (let len = Math.min(toks.length, 5); len >= 1; len--) {
        for (let i = 0; i + len <= toks.length; i++) {
            const sel = resolveModel(b, toks.slice(i, i + len).join(' '));
            if (sel) return { sel, label: modelLabel(b, sel), rest: toks.slice(0, i).concat(toks.slice(i + len)).join(' ') };
        }
    }
    return null;
}

/** How the page names a picker value: the family, or the model without the make. */
export function modelLabel(b, sel) {
    if (!sel) return '';
    return sel[0] === 'f' ? sel.slice(2) : stripMake(b, sel.slice(2));
}

/* An owner's free text → the catalogue's name for that model, when one fits
   (the exact listing, else the family); otherwise the text as written. */
function canonModel(b, text) {
    const s = String(text || '').trim().slice(0, 60);
    const alias = findModel(textKey(s), b.id);
    const hit = findModelIn(b, alias.model || s);
    if (!hit) return s;
    if (hit.sel[0] === 'm') return hit.sel.slice(2);
    const base = modelIndex(b).models.find((m) => m.label.toLowerCase() === hit.label.toLowerCase());
    return base ? base.name : hit.label;
}

/** Does this colour belong to the picked model (a 'f:'/'m:' value)? */
export function inModel(b, r, sel) {
    const ms = r[F.models] || [];
    if (!sel) return true;
    if (sel[0] === 'm') return ms.indexOf(sel.slice(2)) >= 0;
    const famOf = modelIndex(b).famOf, L = sel.slice(2);
    return ms.some((m) => famOf.get(m) === L);
}

/** Models across all makes named by `text` alone («gla», «Солярис» is an
    alias, «camry»). → [{brand, sel, label, rest}] — one per make. */
export function findModelAnywhere(idx, text) {
    const out = [];
    for (const b of idx.brands) {
        const hit = findModelIn(b, text);
        if (hit) out.push({ brand: b.id, ...hit });
    }
    // the longest match wins; two makes with the same model name stay ambiguous
    const len = (h) => modelKey(h.label).length;
    out.sort((a, c) => len(c) - len(a));
    return out.filter((h) => len(h) === (out[0] ? len(out[0]) : 0));
}

function findModel(t, brand) {
    const s = ' ' + t + ' ';
    const keys = Object.keys(MODEL_ALIASES).sort((a, b) => b.length - a.length);
    for (const k of keys) {
        const [b, model] = MODEL_ALIASES[k];
        if (brand && b !== brand) continue;
        if (s.indexOf(' ' + k + ' ') >= 0) return { brand: b, model, rest: s.replace(' ' + k + ' ', ' ').trim() };
    }
    return { brand: null, model: null, rest: t };
}

/* ── what did the person type? ───────────────────────────────────────────── */

const VIN_CHARS = /^[A-Z0-9]{17}$/;

/**
 * → { kind: 'empty' }
 *   { kind: 'vin', vin }
 *   { kind: 'brand', brand, model, modelSel, code? }  make named (maybe with a model, a code)
 *   { kind: 'code', code, models }                     a bare code (models: same word as a model)
 *   { kind: 'unknown', text }
 * `model` is the name as the person wrote it (or the catalogue's label);
 * `modelSel` is the picker value when the make's catalogue knows the model.
 */
export function classify(raw, idx) {
    const s = String(raw == null ? '' : raw).trim();
    if (!s) return { kind: 'empty' };
    const compact = fold(s).replace(/[\s\-]/g, '');
    if (compact.length === 17 && VIN_CHARS.test(compact)) return { kind: 'vin', vin: compact };

    const b = findBrand(s, idx);
    if (b) {
        const bb = idx.byId.get(b.brand);
        let model = b.model, modelSel = model ? resolveModel(bb, model) : null, rest = b.rest;
        /* A model the catalogue knows beats a code guess: «mercedes gla» is
           the GLA, not a code «GLA». What follows a model is a code only when
           the make has that code — «gla 200» is the engine, not paint 200. */
        const hit = findModelIn(bb, rest);
        if (hit) { if (!modelSel) { modelSel = hit.sel; model = hit.label; } rest = hit.rest; }
        /* A Russian model word names the family; the words after it may name
           the version: «веста sw cross» → Vesta SW Cross, «рио x» → Rio X. */
        if (modelSel && !hit && rest) {
            const ext = findModelIn(bb, model + ' ' + rest);
            if (ext && ext.sel !== modelSel && modelKey(ext.label).indexOf(modelKey(model)) === 0) {
                modelSel = ext.sel; model = ext.label; rest = ext.rest;
            }
        }
        let code = codeCandidate(rest);
        if (code && modelSel && !lookupCode(code, idx, bb.id).exact.length) code = null;
        return { kind: 'brand', brand: b.brand, model, modelSel, code };
    }
    const code = codeCandidate(s);
    /* A bare word can be a model of one make («gla», «camry») and a code of
       another: a known code is answered as a code, with the model offered. */
    const models = findModelAnywhere(idx, textKey(s));
    if (code && idx.codeIdx.has(codeKey(code))) return { kind: 'code', code, models };
    if (models.length === 1 && !models[0].rest) {
        return { kind: 'brand', brand: models[0].brand, model: models[0].label, modelSel: models[0].sel, code: null };
    }
    if (code) return { kind: 'code', code, models };
    return { kind: 'unknown', text: s };
}

/* A token that can be a paint code: 2–8 letters/digits (with an optional
   hyphen, as Honda prints them), not a plain year, not a word. */
function codeCandidate(rest) {
    const toks = String(rest || '').split(/[\s,;]+/).filter(Boolean);
    for (const t of toks) {
        const k = codeKey(t);
        if (!/^[A-Z0-9ЗБГДЖИЙЛПФЦЧШЩЪЫЬЭЮЯ]{2,8}$/.test(k)) continue;
        if (/^(19|20)\d{2}$/.test(k)) continue;             // a model year
        if (/^[A-ZА-Я]{5,}$/.test(k) && !/\d/.test(k)) continue;  // a word, not a code
        return t.trim();
    }
    return null;
}

/* ── code lookup ─────────────────────────────────────────────────────────── */

/**
 * Which factory colours can this code be?
 * → { exact: [{brand, row}], near: [{brand, row, via}] }
 * `near` is filled only when nothing matched exactly: a label read as «1G3»
 * vs «IG3» is a question to ask, not an answer to give.
 */
export function lookupCode(code, idx, brand) {
    const k = codeKey(code);
    const pick = (hits) => (brand ? hits.filter((h) => h.brand === brand) : hits);
    const exact = pick(idx.codeIdx.get(k) || []);
    if (exact.length) return { exact, near: [] };
    const near = [];
    for (const v of lookalikeVariants(k)) {
        for (const h of pick(idx.codeIdx.get(v) || [])) near.push({ ...h, via: v });
    }
    return { exact: [], near };
}

/** Does this string look like a paint code of that make? null — the make has
    no pattern to compare with. The pattern is written for the code AS PRINTED
    (Honda «NH-731P», Chevrolet «WA-9260»), UAZ codes are Cyrillic — so the
    printed, the raw and the bare forms are all tried. Same rule on the server:
    CarPaintCommunityController::fitsMakeFormat. */
export function fitsBrand(code, b) {
    if (!b || !b.label || !b.label.codeRegex) return null;
    let re;
    try { re = new RegExp(b.label.codeRegex); } catch (e) { return null; }
    const raw = String(code == null ? '' : code).trim().toUpperCase().replace(/\s+/g, '');
    return re.test(raw) || re.test(fold(raw)) || re.test(codeKey(raw));
}

/* ── reading a photographed label ────────────────────────────────────────── */

/* Captions printed next to the paint code, across makes and languages. A
   code on the same line as one of these — or right after it — is the
   strongest evidence a label gives. */
const CAPTIONS = [
    ['C/TR', /\bC\s*[\/|]\s*TR\b/], ['EXT PNT', /\bEXT\.?\s*P(?:AI)?NT\b/], ['PAINT', /\bPAINT\b/],
    ['PNT', /\bPNT\b/], ['COLOR', /\bCOLOU?R\b/], ['BC/CC', /\bBC\s*[\/|]\s*CC\b/],
    ['LACK', /\bLACK\b/], ['FARBE', /\bFARBE\b/], ['PEINTURE', /\bPEINTURE\b/], ['OV', /\bOV\s*(?=[A-Z0-9]{3})/],
    ['ЦВЕТ', /ЦВЕТ/], ['ЭМАЛЬ', /ЭМАЛ/], ['КРАСКА', /КРАСК/], ['EXT', /\bEXT\b/],
];

/* Maker names as they appear printed on labels. */
const LABEL_BRANDS = [
    ['toyota', /\bTOYOTA\b/], ['lexus', /\bLEXUS\b/], ['nissan', /\bNISSAN\b/], ['infiniti', /\bINFINITI\b/],
    ['mazda', /\bMAZDA\b/], ['mitsubishi', /\bMITSUBISHI\b/], ['honda', /\bHONDA\b/], ['subaru', /\bSUBARU\b/],
    ['suzuki', /\bSUZUKI\b/], ['hyundai', /\bHYUNDAI\b/], ['kia', /\bKIA\b/], ['volkswagen', /\bVOLKSWAGEN\b|\bVW\b/],
    ['skoda', /\bSKODA\b|(?:^|[^A-ZŠ0-9])ŠKODA(?![A-Z0-9])/], ['audi', /\bAUDI\b/], ['renault', /\bRENAULT\b/], ['ford', /\bFORD\b/],
    ['bmw', /\bBMW\b/], ['mercedes', /\bMERCEDES|DAIMLER\b/], ['chevrolet', /\bCHEVROLET\b|\bGENERAL MOTORS\b/],
    ['opel', /\bOPEL\b/], ['peugeot', /\bPEUGEOT\b/], ['citroen', /\bCITRO[EË]N\b/], ['lada', /\bLADA\b|АВТОВАЗ|AVTOVAZ/],
    /* \b is ASCII-only without the u flag — «\bУАЗ\b» never matched. And GAZ
       in Latin is a Chevrolet paint code (Summit White): GAZ's own labels are
       Cyrillic, so only «ГАЗ» names the make. The boundary before the word is
       a group, not a lookbehind: iOS Safari < 16.4 cannot parse lookbehind
       and would drop this whole file (NoRegexLookbehindTest); these patterns
       are only ever .test()ed, so capturing the preceding character is free. */
    ['uaz', /\bUAZ\b|(?:^|[^А-ЯA-Z0-9])УАЗ(?![А-ЯA-Z0-9])/], ['gaz', /(?:^|[^А-ЯA-Z0-9])ГАЗ(?![А-ЯA-Z0-9])/],
    ['haval', /\bHAVAL\b|GREAT\s*WALL/], ['chery', /\bCHERY\b/],
    ['geely', /\bGEELY\b/], ['omoda', /\bOMODA\b/], ['exeed', /\bEXEED\b/], ['changan', /\bCHANGAN\b/],
];

/**
 * lines: OCR lines [{text, box?, conf?}] in reading order.
 * decodeVin: optional (vin) → {valid, manufacturer} — the VIN module's
 *   decode(), injected so node tests do not need it.
 * → { brand, brandVia, vin, candidates: [{code, score, line, why, hits}] }
 *   candidates are sorted, best first; `hits` = catalogue rows for the brand.
 */
export function readLabel(lines, idx, opts) {
    const o = opts || {};
    /* Two views of every line: `up` folds Cyrillic look-alikes to Latin (so
       «С/TR» read as Cyrillic still says C/TR), `cu` keeps the script as
       printed (so «ЦВЕТ» and «АВТОВАЗ» stay words: folded they become
       «ЦBET» and «ABTOBAЗ» and match nothing). */
    const L = (lines || []).map((l, i) => ({ i, raw: String(l.text || ''), up: fold(l.text || ''),
        cu: String(l.text || '').toUpperCase().replace(/Ё/g, 'Е'), box: l.box || null, conf: l.conf }));
    const all = L.map((l) => l.up).join('\n');
    const allCu = L.map((l) => l.cu).join('\n');

    // 1. The make: a VIN on the label beats a printed name, a printed name
    //    beats the make the person chose (a label is evidence, a click is a guess).
    //    Except a plant VIN (PLANT_MAKES): it names the plant, and so does the
    //    printed name (a Kia Rio from Hyundai's St Petersburg plant says
    //    HYUNDAI on its label) — there the code on the label decides.
    let brand = null, brandVia = null, vin = null, plant = null;
    const vinM = all.replace(/[ \t]/g, '').match(/[A-HJ-NPR-Z0-9]{17}/);
    if (vinM && o.decodeVin) {
        const d = o.decodeVin(vinM[0]);
        if (d && d.valid) {
            vin = vinM[0];
            const makes = (PLANT_MAKES[vin.slice(0, 3)] || []).filter((id) => idx.byId.has(id));
            if (makes.length) plant = makes;
            else {
                const b = brandFromManufacturer(d.manufacturer);
                if (b && idx.byId.has(b)) { brand = b; brandVia = 'vin'; }
            }
        }
    }
    let printed = null;
    for (const [id, re] of LABEL_BRANDS) if ((re.test(all) || re.test(allCu)) && idx.byId.has(id)) { printed = id; break; }

    // 2. Candidates: every short token, scored for one make.
    const score = (brandId) => {
        const cands = new Map();
        const push = (code, sc, line, why) => {
            const k = codeKey(code);
            if (!k || k.length < 2 || k.length > 8) return;
            if (vin && vin.indexOf(k) >= 0 && k.length > 4) return;   // a slice of the VIN
            const cur = cands.get(k);
            if (!cur || cur.score < sc) cands.set(k, { code: k, raw: String(code).toUpperCase(), score: sc, line, why });
        };
        const b = brandId ? idx.byId.get(brandId) : null;
        const re = b && b.label && b.label.codeRegex ? safeRe(b.label.codeRegex) : null;

        for (const l of L) {
            const capHit = CAPTIONS.find(([, rx]) => rx.test(l.up) || rx.test(l.cu));
            const nextLine = L[l.i + 1];
            // Brand-specific shape first: Toyota «C/TR 040/FB13» → 040.
            const special = specialExtract(brandId, l.up, l.cu);
            if (special) push(special, 9, l, 'rule');
            if (capHit) {
                const src = capHit[1].test(l.up) ? l.up : l.cu;
                const after = src.split(capHit[1]).slice(1).join(' ');
                for (const tok of tokens(after)) push(tok, 6, l, 'caption:' + capHit[0]);
                // the code is often printed UNDER the caption, not beside it
                if (nextLine) for (const tok of tokens(nextLine.up).slice(0, 3)) push(tok, 4, nextLine, 'below:' + capHit[0]);
            }
            const toks = tokens(l.up);
            for (let t = 0; t < toks.length; t++) {
                // «MFD. BY TOYOTA» opens every FMVSS label, and BY is a Ford code:
                // the word after «manufactured» is boilerplate, not a code
                if (toks[t] === 'BY' && t > 0 && MADE_WORD.test(toks[t - 1])) continue;
                push(toks[t], 1, l, 'token');
            }
        }
        // A code with no caption, found by WHERE it sits (Geely: «bottom-right
        // corner of the plate» — geely-motors.com, example A08). Geely
        // publishes no colour list, so no catalogue hit can vouch for it.
        const corner = layoutExtract(brandId, L);
        if (corner) push(corner.code, 9, corner.line, 'corner');

        // 3. Evidence from the catalogue and the make's code shape.
        const out = [];
        for (const c of cands.values()) {
            let sc = c.score;
            const hits = b ? lookupCode(c.code, idx, b.id).exact : [];
            if (hits.length) sc += 6;
            // the pattern is written for the code AS PRINTED (Honda «NH-731P»),
            // the key has its separators stripped — either form may fit
            if (re) sc += re.test(c.raw) || re.test(c.code) ? 2 : -3;
            if (/^\d{4}$/.test(c.code) && /^(19|20)/.test(c.code)) sc -= 4;    // a year
            if (/^\d{5,}$/.test(c.code)) sc -= 3;                                // a part/serial number
            if (/^(KG|KGS|LBS|KW|PSI|KPA|MM|HP|CC)$/.test(c.code)) sc -= 6;      // units
            // shown as the maker prints it («B-515M», not the key «B515M»)
            const shown = hits.length ? hits[0].row[F.code] : (c.raw && codeKey(c.raw) === c.code ? c.raw : c.code);
            out.push({ code: shown, key: c.code, score: sc, line: c.line ? { i: c.line.i, text: c.line.raw, box: c.line.box } : null, why: c.why, hits });
        }
        out.sort((x, y) => y.score - x.score);
        /* The best reading names no code this make has, but one look-alike of it
           does (Kia «PAINT: AZS» from a far photo; Kia has A2S): offer that code
           as the NEXT chip, marked by the catalogue. Never in front of what the
           label says — an O read as 0 is a different colour (lookupCode). */
        const top = out[0];
        if (b && top && !top.hits.length && top.score >= 5 && !out.some((c) => c.hits.length)) {
            const near = lookupCode(top.key, idx, b.id).near;
            if (near.length === 1) {
                const h = near[0];
                out.splice(1, 0, { code: h.row[F.code], key: codeKey(h.row[F.code]), score: top.score - 1, line: top.line, why: 'lookalike:' + top.key, hits: [h] });
            }
        }
        return out.filter((c) => c.score >= 5).slice(0, 5);
    };

    if (plant) {
        // The make whose catalogue holds the label's best code; on a tie the
        // printed name, then the person's choice, then the plant's first make.
        const pref = [printed, o.brand].filter((id) => id && plant.includes(id));
        let best = null;
        for (const id of plant) {
            const c = score(id);
            const top = c[0] && c[0].hits.length ? c[0].score : -1;
            const rank = top * 10 - (pref.includes(id) ? pref.indexOf(id) - 2 : 0);
            if (!best || rank > best.rank) best = { id, c, rank };
        }
        return { brand: best.id, brandVia: 'vin', vin, candidates: best.c };
    }
    if (!brand && printed) { brand = printed; brandVia = 'label'; }
    if (brand) return { brand, brandVia, vin, candidates: score(brand) };

    /* Nothing on the label names the make — a Honda colour tag carries no
       name and no VIN. The make the person opened last is a guess (they may
       have looked one car up and photographed another), so let the label
       answer: the make whose rules and catalogue explain its best code, if
       that make wins clearly. The chosen make only settles a tie; an unclear
       field leaves the make unknown rather than guessed. */
    const chosen = o.brand && idx.byId.has(o.brand) ? o.brand : null;
    const ranked = [];
    for (const b of idx.brands) {
        const c = score(b.id);
        if (c[0] && c[0].hits.length) ranked.push({ id: b.id, c, s: c[0].score + (b.id === chosen ? 1 : 0) });
    }
    ranked.sort((x, y) => y.s - x.s);
    const top = ranked[0], next = ranked[1];
    /* A bare word is no evidence of the make: «MFD. BY TOYOTA» read from a far
       photo as «FD. BY ToY / OTA…» made Ford the make (BY is a Ford code) and,
       being a catalogue hit, stopped the page before the zoom and tile passes
       that read the real «C/TR 1E9». Only a caption, a maker's rule or a fixed
       place may name the make from the code alone. */
    if (top && top.c[0].score >= 9 && top.c[0].why !== 'token' && (!next || top.s - next.s >= 2)) {
        return { brand: top.id, brandVia: top.id === chosen ? 'chosen' : 'label', vin, candidates: top.c };
    }
    if (chosen) return { brand: chosen, brandVia: 'chosen', vin, candidates: score(chosen) };
    return { brand: null, brandVia: null, vin, candidates: score(null) };
}

/* One WMI, several makes: the VIN names the plant. Z94 — Hyundai Motor
   Manufacturing Rus (Solaris, Creta AND Kia Rio); Z8T — PSMA Rus, Kaluga
   (Mitsubishi, Peugeot, Citroën); XW8 — Volkswagen Group Rus (VW, Škoda,
   Audi). */
const PLANT_MAKES = {
    Z94: ['hyundai', 'kia'],
    Z8T: ['mitsubishi', 'peugeot', 'citroen'],
    XW8: ['volkswagen', 'skoda', 'audi'],
};

function safeRe(s) { try { return new RegExp(s); } catch (e) { return null; } }

/* «MFD. BY», «MFG BY», «MANUFACTURED BY» — and «FD. BY», what a far photo
   leaves of «MFD.» (bench «far», 28.10). */
const MADE_WORD = /^(?:M?F[DG]|MANUFACTURED|MADE|BUILT|ASSEMBLED|PRODUCED)$/;

function tokens(s) {
    return String(s || '').split(/[\s:;,|()\[\]]+/).map((t) => t.replace(/^[\/\-.]+|[\/\-.]+$/g, ''))
        .filter((t) => t.length >= 2 && t.length <= 9 && /[A-Z0-9ЗБГДЖИЙЛПФЦЧШЩЪЫЬЭЮЯ]/.test(t));
}

/* Makers that print the code WITHOUT a caption, at a fixed place on the
   plate. Needs line boxes (OCR gives them; typed lines have none). The
   token must look like a code — letters AND digits, 2–4 characters — so a
   weight («1044 kg») or a slice of a certificate number never qualifies. */
const CORNER_BRANDS = { geely: 'bottom-right' };

function layoutExtract(brand, L) {
    if (CORNER_BRANDS[brand] !== 'bottom-right') return null;
    let best = null;
    for (const l of L) {
        if (!l.box) continue;
        for (const tok of tokens(l.up)) {
            const k = codeKey(tok);
            if (!/^(?=.*[A-Z])(?=.*\d)[A-Z0-9]{2,4}$/.test(k)) continue;
            if (/^(KG|KGS|LBS|MM)$/.test(k)) continue;
            const bottom = l.box.y + l.box.h, right = l.box.x + l.box.w;
            if (!best || bottom > best.bottom + l.box.h * 0.5 || (Math.abs(bottom - best.bottom) <= l.box.h * 0.5 && right > best.right)) {
                best = { code: tok, line: l, bottom, right };
            }
        }
    }
    return best;
}

/* Label layouts where the position of the code is fixed by the maker. */
function specialExtract(brand, up, cu) {
    let m;
    if ((brand === 'toyota' || brand === 'lexus' || !brand) && (m = up.match(/\bC\s*[\/|]\s*TR\s*[:.]?\s*([0-9A-Z]{3})\s*[\/|]/))) return m[1];
    if ((brand === 'toyota' || brand === 'lexus') && (m = up.match(/\bC\s*[\/|]\s*TR\s*[:.]?\s*([0-9A-Z]{3})\b/))) return m[1];
    if ((brand === 'hyundai' || brand === 'kia') && (m = up.match(/\b(?:PAINT|PNT|EXT\.?\s*COLOU?R)\s*[:.]?\s*([A-Z0-9]{2,3})\b/))) return m[1];
    if (brand === 'ford' && (m = up.match(/\bEXT\.?\s*PNT\s*[:.]?\s*([A-Z0-9]{2})\b/))) return m[1];
    if ((brand === 'nissan' || brand === 'infiniti') && (m = up.match(/\bCOLOU?R(?:\s*[\/|]\s*TRIM)?\s*[:.]?\s*([A-Z0-9]{3})\b/))) return m[1];
    if (brand === 'mazda' && (m = up.match(/\bCOLOU?R\s*[:.]?\s*([A-Z0-9]{2,3})\b/))) return m[1];
    if (brand === 'renault' && (m = up.match(/\bOV\s*([A-Z0-9]{3,5})\b/))) return m[1];
    if (brand === 'lada' && (m = String(cu || '').match(/(?:ЦВЕТ|ЭМАЛ[ЬИ]?|КРАСК[АИ])[^0-9\n]{0,24}(\d{3})(?!\d)/))) return m[1];
    if (brand === 'chevrolet' && (m = up.match(/\bWA\s*-?\s*(\d{3,4}[A-Z]?)\b/))) return 'WA' + m[1];
    // Honda prints «NH-731P»; OCR may read the hyphen as a space and split it
    if (brand === 'honda' && (m = up.match(/\b(NH|YR|BG|PB|RP|GY|R|B|G|Y|P)\s*-?\s*(\d{1,4}[A-Z]{0,2})\b/))) return m[1] + '-' + m[2];
    return null;
}

/** The VIN module names makers its own way ('AvtoVAZ (Lada)', 'Toyota|us',
    'Great Wall / Haval'); map them to catalogue ids. */
export function brandFromManufacturer(m) {
    if (!m) return null;
    const n = textKey(String(m).split('|')[0]);
    const MAP = [
        [/lada|avtovaz/, 'lada'], [/^uaz/, 'uaz'], [/^gaz/, 'gaz'], [/gm-avtovaz/, 'chevrolet-niva'],
        [/volkswagen/, 'volkswagen'], [/skoda/, 'skoda'], [/^audi/, 'audi'], [/mercedes/, 'mercedes'],
        [/^bmw/, 'bmw'], [/^mini$/, 'mini'], [/toyota/, 'toyota'], [/lexus/, 'lexus'], [/nissan/, 'nissan'],
        [/infiniti/, 'infiniti'], [/mazda/, 'mazda'], [/mitsubishi/, 'mitsubishi'], [/honda/, 'honda'],
        [/subaru/, 'subaru'], [/suzuki/, 'suzuki'], [/hyundai/, 'hyundai'], [/^kia/, 'kia'],
        [/renault/, 'renault'], [/dacia/, 'renault'], [/ford/, 'ford'], [/chevrolet|gm korea|daewoo/, 'chevrolet'],
        [/opel/, 'opel'], [/peugeot/, 'peugeot'], [/citroen/, 'citroen'], [/haval|great wall/, 'haval'],
        [/chery/, 'chery'], [/geely/, 'geely'], [/changan/, 'changan'],
    ];
    for (const [re, id] of MAP) if (re.test(n)) return id;
    return null;
}

/* ── colour rows for display ─────────────────────────────────────────────── */

/** Filter a make's colours by free text (code, any name, model), family and
    model (a picker value, or a model name — resolved against the catalogue;
    a model the catalogue does not know filters nothing). Colours listed
    without any model are left out of a model's view: nobody said they were
    offered on it. */
export function filterColors(b, q, family, model) {
    const k = textKey(q || '');
    const ck = codeKey(q || '');
    const sel = model ? resolveModel(b, model) : null;
    return b.colors.filter((r) => {
        if (family && r[F.family] !== family) return false;
        if (sel && !inModel(b, r, sel)) return false;
        if (!k) return true;
        if (codeKey(r[F.code]).indexOf(ck) === 0 || (r[F.codeAlt] && codeKey(r[F.codeAlt]).indexOf(ck) === 0)) return true;
        const hay = textKey([r[F.name], r[F.nameEn], r[F.nameRu], (r[F.altNames] || []).join(' '), (r[F.models] || []).join(' ')].join(' '));
        return hay.indexOf(k) >= 0;
    });
}

/** Families present among `rows` (default: the whole make), for the filter
    chips, with how many colours each. */
export function familiesOf(b, rows) {
    const n = {};
    for (const r of rows || b.colors) n[r[F.family]] = (n[r[F.family]] || 0) + 1;
    // most colours first; «other» is a leftover, never a headline
    return Object.keys(n).sort((a, c) => (a === 'other') - (c === 'other') || n[c] - n[a]).map((f) => ({ f, n: n[f] }));
}

/* ── where a row comes from ──────────────────────────────────────────────── */

const TWO_LEVEL = new Set(['co.jp', 'ne.jp', 'or.jp', 'co.kr', 'co.uk', 'org.uk', 'com.au', 'com.br', 'com.mx', 'com.cn',
    'com.tr', 'co.za', 'com.ar', 'co.id', 'com.my', 'com.sg', 'co.th', 'com.tw', 'com.ua', 'co.in', 'com.co']);
/* Hosting that is not the publisher: the maker's site links the file there. */
const FILE_HOSTS = new Set(['perxis.ru', 'windows.net']);

/** A host as people know the site: «static.oneweb.mercedes-benz.com» →
    «mercedes-benz.com», «www3.nissan.co.jp» → «nissan.co.jp». */
export function siteOf(host) {
    const h = String(host || '').toLowerCase().replace(/:\d+$/, '').replace(/^www\d*\./, '');
    const p = h.split('.');
    if (p.length <= 2) return h;
    return TWO_LEVEL.has(p.slice(-2).join('.')) ? p.slice(-3).join('.') : p.slice(-2).join('.');
}

/**
 * What a source line says about a URL, as plain text (the page links
 * nowhere): the publisher's site — an archived copy names the site it
 * copied, with the year of the copy — or `fileHost` when the file sits on a
 * hosting service the maker's own site links to.
 * → { site, year, fileHost }
 */
export function sourceInfo(url) {
    let u;
    try { u = new URL(url); } catch (e) { return { site: String(url || ''), year: null, fileHost: false }; }
    let host = u.host, year = null;
    if (/(^|\.)archive\.org$/.test(u.host)) {
        const m = /^\/web\/(\d{4})\d*(?:[a-z]{2}_)?\/(.+)$/.exec(u.pathname + u.search);
        if (m) {
            year = +m[1];
            try { host = new URL(/^https?:/i.test(m[2]) ? m[2] : 'http://' + m[2]).host; } catch (e) { /* keep archive host */ }
        }
    }
    const site = siteOf(host);
    return { site, year, fileHost: FILE_HOSTS.has(site) };
}

/** An address to type by hand: host and path, no scheme, no «www.». */
export function plainAddress(url) {
    try {
        const u = new URL(url);
        return (u.host.replace(/^www\./, '') + u.pathname).replace(/\/$/, '');
    } catch (e) { return String(url || ''); }
}
